import { Theme, createStyles } from "@material-ui/core";

export const MONTHLY_REPORT_PALETTE = {
  primary50: "#FFF3EC",
  primary100: "#FFE0CC",
  primary200: "#FFC199",
  primary300: "#FFA366",
  primary400: "#FF7A26",
  primary500: "#FB5700",
  primary600: "#D94C00",

  dark100: "#C7C1DA",
  dark300: "#7D73AA",
  dark400: "#584C91",
  dark500: "#170036",
  dark700: "#0E0023",

  teal50: "#E6F3F4",
  teal100: "#B0D4D8",
  teal200: "#7AB5BD",
  teal300: "#4496A1",
  teal400: "#1F7F8C",
  teal500: "#02272F",
  teal600: "#022228",

  accent100: "#FDEFC2",
  accent300: "#F8DB7A",
  accent400: "#F5D156",
  accent500: "#EEBE41",

  purple500: "#6B0F3C",

  info300: "#99D1D5",
  info400: "#8AC5C7",
  info500: "#6FAFB1",

  gray50: "#F8FAFA",
  gray100: "#F1F4F4",
  gray200: "#E4E8E8",
  gray300: "#DBDFDF",
  gray500: "#A0A5A5",
  gray600: "#7C8080",
  gray700: "#5A5D5D",

  textPrimary: "#170036",
  textSecondary: "#7C8080",
  textMuted: "#A0A5A5",
  surface: "#FFFFFF",
  border: "#E4E8E8"
} as const;

export default (theme: Theme) =>
  createStyles({
    page: {
      background: MONTHLY_REPORT_PALETTE.gray50,
      minHeight: "100%",
      paddingBottom: theme.spacing(4)
    },

    topBar: {
      marginBottom: theme.spacing(3)
    },

    controlsCard: {
      background: MONTHLY_REPORT_PALETTE.surface,
      borderRadius: 16,
      border: `1px solid ${MONTHLY_REPORT_PALETTE.border}`,
      boxShadow: theme.shadows[1],
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(3)
    },

    controlsTitle: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 1.5,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.primary500,
      marginBottom: theme.spacing(2)
    },

    controlField: {
      minWidth: 180,
      "& .MuiOutlinedInput-root": {
        backgroundColor: MONTHLY_REPORT_PALETTE.surface
      }
    },

    controlFieldWide: {
      minWidth: 260,
      "& .MuiOutlinedInput-root": {
        backgroundColor: MONTHLY_REPORT_PALETTE.surface
      }
    },

    contentWrapper: {
      marginTop: "20px"
    },

    exportRoot: {
      background: "#FFFFFF"
    },

    exportMode: {
      width: "736px",
      maxWidth: "736px",
      margin: "0 auto",
      boxSizing: "border-box",
      overflow: "hidden",
      "& $reportPage": {
        borderRadius: 0,
        boxShadow: "none",
        overflow: "hidden"
      },
      "& $pageInner": {
        padding: "14px 12px 14px 12px"
      },
      "& $grid2": {
        marginBottom: 6
      },
      "& $reportHeader": {
        marginBottom: 24,
        paddingBottom: 16
      },
      "& $card": {
        height: "auto",
        minHeight: 250,
        padding: 14
      },
      "& $tableCard": {
        padding: 14
      },
      "& $chartBox": {
        height: 176,
        position: "relative"
      },
      "& $chartBoxTall": {
        height: 196
      },
      "& $chartBoxDonut": {
        height: 176
      },
      "& $heatmapPageContent": {
        width: "100%"
      },
      "& .MuiGrid-container": {
        width: "100% !important",
        margin: "0 !important"
      },
      "& .MuiGrid-item": {
        padding: "6px !important",
        boxSizing: "border-box"
      },
      "& .MuiGrid-grid-md-6": {
        flexBasis: "49.2%",
        maxWidth: "49.2%"
      },
      "& canvas": {
        maxWidth: "100% !important",
        maxHeight: "100% !important",
        width: "100% !important"
      }
    },

    reportPage: {
      background: MONTHLY_REPORT_PALETTE.surface,
      borderRadius: 16,
      boxShadow: theme.shadows[2],
      overflow: "hidden"
    },

    pageInner: {
      padding: "26px 24px 32px 24px"
    },

    printContainer: {
      position: "absolute",
      left: "-100000px",
      top: 0,
      width: "900px",
      zIndex: -1,
      pointerEvents: "none"
    },

    printPage: {
      background: "#FFFFFF",
      width: "100%",
      boxShadow: "none",
      borderRadius: 0,
      overflow: "visible"
    },

    printPageInner: {
      padding: "0"
    },

    printAvoidBreak: {
      breakInside: "avoid",
      pageBreakInside: "avoid"
    },

    printPageBreakBefore: {
      breakBefore: "page",
      pageBreakBefore: "always"
    },

    exportPageSection: {
      breakInside: "avoid",
      pageBreakInside: "avoid"
    },

    exportPageBreakBefore: {
      breakBefore: "page",
      pageBreakBefore: "always"
    },

    reportHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 48,
      paddingBottom: 32,
      borderBottom: `1px solid ${MONTHLY_REPORT_PALETTE.border}`
    },

    reportHeaderLeft: {
      flex: 1,
      minWidth: 0
    },

    reportHeaderRight: {
      textAlign: "right",
      marginLeft: 24,
      flexShrink: 0
    },

    reportHeaderPrint: {
      alignItems: "flex-start"
    },

    reportHeaderRightPrint: {
      marginLeft: 12,
      maxWidth: 160
    },

    reportTitlePrint: {
      fontSize: 23,
      lineHeight: 1.12
    },

    logoImage: {
      width: 175,
      height: "auto",
      display: "block",
      marginLeft: "auto"
    },

    logoImagePrint: {
      width: 130
    },

    logoPrint: {
      fontSize: 17
    },

    brand: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 2,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.primary500,
      marginBottom: 6
    },

    reportTitle: {
      fontSize: 30,
      fontWeight: 800,
      color: MONTHLY_REPORT_PALETTE.textPrimary,
      letterSpacing: -0.5,
      lineHeight: 1.2
    },

    reportSubtitle: {
      fontSize: 14,
      color: MONTHLY_REPORT_PALETTE.textSecondary,
      marginTop: 4
    },

    logo: {
      fontSize: 26,
      fontWeight: 800,
      color: MONTHLY_REPORT_PALETTE.textPrimary,
      letterSpacing: -0.5
    },

    logoAccent: {
      color: MONTHLY_REPORT_PALETTE.primary500
    },

    meta: {
      fontSize: 12,
      color: MONTHLY_REPORT_PALETTE.textMuted,
      marginTop: 4
    },

    dateBadgeWrapper: {
      marginTop: 6
    },

    dateBadge: {
      display: "inline-block",
      background: MONTHLY_REPORT_PALETTE.primary50,
      color: MONTHLY_REPORT_PALETTE.primary600,
      fontWeight: 600,
      fontSize: 12,
      padding: "4px 12px",
      borderRadius: 20
    },

    section: {
      marginTop: 0,
      marginBottom: 20
    },

    sectionTag: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 2,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.primary500,
      marginBottom: 4
    },

    sectionTitle: {
      fontSize: 22,
      fontWeight: 700,
      color: MONTHLY_REPORT_PALETTE.textPrimary,
      letterSpacing: -0.3
    },

    sectionDesc: {
      fontSize: 13,
      color: MONTHLY_REPORT_PALETTE.textSecondary,
      marginTop: 2,
      marginBottom: 20
    },

    kpiGrid: {
      marginBottom: 28
    },

    kpiRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 16,
      marginBottom: 28
    },

    kpiRowPrint: {
      flexWrap: "nowrap",
      gap: 10,
      paddingRight: 4
    },

    kpiItem: {
      flex: "0 0 calc(20% - 13px)",
      maxWidth: "calc(20% - 13px)",
      paddingRight: "10px"
    },

    kpiCard: {
      background: MONTHLY_REPORT_PALETTE.surface,
      borderRadius: 16,
      padding: "18px 14px",
      border: `1px solid ${MONTHLY_REPORT_PALETTE.border}`,
      height: "100%",
      boxShadow: "0 2px 4px rgba(0,0,0,0.04)"
    },

    kpiCardPrimary: {
      borderTop: `3px solid ${MONTHLY_REPORT_PALETTE.teal500}`
    },

    kpiCardSuccess: {
      borderTop: `3px solid ${MONTHLY_REPORT_PALETTE.teal400}`
    },

    kpiCardDanger: {
      borderTop: `3px solid ${MONTHLY_REPORT_PALETTE.primary500}`
    },

    kpiCardInfo: {
      borderTop: `3px solid ${MONTHLY_REPORT_PALETTE.info500}`
    },

    kpiCardWarning: {
      borderTop: `3px solid ${MONTHLY_REPORT_PALETTE.accent500}`
    },

    kpiVal: {
      fontSize: 30,
      fontWeight: 800,
      letterSpacing: -1,
      lineHeight: 1
    },

    kpiValPrimary: {
      color: MONTHLY_REPORT_PALETTE.teal500
    },

    kpiValSuccess: {
      color: MONTHLY_REPORT_PALETTE.teal400
    },

    kpiValDanger: {
      color: MONTHLY_REPORT_PALETTE.primary500
    },

    kpiValInfo: {
      color: MONTHLY_REPORT_PALETTE.purple500
    },

    kpiValWarning: {
      color: MONTHLY_REPORT_PALETTE.accent500
    },

    kpiLabel: {
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: 1,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.textSecondary,
      marginTop: 6
    },

    highlights: {
      background: `linear-gradient(135deg, ${MONTHLY_REPORT_PALETTE.dark500}, ${MONTHLY_REPORT_PALETTE.dark700})`,
      borderRadius: 16,
      padding: "24px 28px",
      marginBottom: 24,
      color: MONTHLY_REPORT_PALETTE.surface
    },

    highlightsTitle: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 1.5,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.accent400,
      marginBottom: 12
    },

    highlightsList: {
      margin: 0,
      padding: 0,
      listStyle: "none"
    },

    highlightsItem: {
      fontSize: 13,
      color: "#E4E8E8",
      padding: "4px 0",
      display: "flex",
      alignItems: "flex-start",
      lineHeight: 1.5
    },

    highlightsArrow: {
      color: MONTHLY_REPORT_PALETTE.primary300,
      fontWeight: 700,
      marginRight: 8,
      flexShrink: 0
    },

    grid2: {
      marginBottom: 16
    },

    card: {
      background: MONTHLY_REPORT_PALETTE.surface,
      borderRadius: 16,
      padding: 24,
      border: `1px solid ${MONTHLY_REPORT_PALETTE.border}`,
      height: "100%"
    },

    tableCard: {
      background: MONTHLY_REPORT_PALETTE.surface,
      borderRadius: 16,
      padding: 24,
      border: `1px solid ${MONTHLY_REPORT_PALETTE.border}`,
      overflow: "hidden"
    },

    cardTitle: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.dark400,
      marginBottom: 16,
      display: "flex",
      alignItems: "center"
    },

    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      display: "inline-block",
      flexShrink: 0,
      marginRight: 10
    },

    dotPrimary: {
      backgroundColor: MONTHLY_REPORT_PALETTE.primary500
    },

    dotInfo: {
      backgroundColor: MONTHLY_REPORT_PALETTE.info400
    },

    dotPurple: {
      backgroundColor: MONTHLY_REPORT_PALETTE.purple500
    },

    dotAccent: {
      backgroundColor: MONTHLY_REPORT_PALETTE.accent500
    },

    dotDark: {
      backgroundColor: MONTHLY_REPORT_PALETTE.dark500
    },

    dotTeal: {
      backgroundColor: MONTHLY_REPORT_PALETTE.teal500
    },

    dotInfoDark: {
      backgroundColor: MONTHLY_REPORT_PALETTE.info500
    },

    chartBox: {
      height: 220,
      position: "relative"
    },

    chartBoxTall: {
      height: 250
    },

    chartBoxDonut: {
      height: 260
    },

    tableWrapper: {
      overflowX: "auto"
    },

    tableHeadCell: {
      textAlign: "left",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      color: MONTHLY_REPORT_PALETTE.textSecondary,
      padding: "10px 14px",
      borderBottom: `2px solid ${MONTHLY_REPORT_PALETTE.border}`,
      background: MONTHLY_REPORT_PALETTE.gray50
    },

    tableHeadCellCentered: {
      textAlign: "center"
    },

    tableBodyCell: {
      padding: "12px 14px",
      borderBottom: `1px solid ${MONTHLY_REPORT_PALETTE.gray100}`,
      color: MONTHLY_REPORT_PALETTE.gray700,
      verticalAlign: "middle"
    },

    tableBodyCellCentered: {
      textAlign: "center"
    },

    communityName: {
      fontWeight: 600,
      color: MONTHLY_REPORT_PALETTE.textPrimary
    },

    pill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 28,
      padding: "2px 10px",
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1
    },

    pillGreen: {
      background: MONTHLY_REPORT_PALETTE.teal50,
      color: MONTHLY_REPORT_PALETTE.teal600
    },

    pillRed: {
      background: MONTHLY_REPORT_PALETTE.primary50,
      color: MONTHLY_REPORT_PALETTE.primary600
    },

    pillZero: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 28,
      color: MONTHLY_REPORT_PALETTE.textMuted,
      fontSize: 12,
      lineHeight: 1
    },

    heatmapWrapper: {
      border: `1px solid ${MONTHLY_REPORT_PALETTE.border}`,
      borderRadius: 16,
      padding: 24,
      background: MONTHLY_REPORT_PALETTE.surface
    },

    heatmapPageContent: {
      width: "90%",
      margin: "0 auto"
    },

    heatmapScroll: {
      overflowX: "auto"
    },

    heatmapGrid: {
      display: "grid",
      gridTemplateColumns: "55px repeat(7, 1fr)",
      gap: 3,
      minWidth: 620
    },

    heatmapHeader: {
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: MONTHLY_REPORT_PALETTE.textSecondary,
      textAlign: "center",
      padding: "5px 0"
    },

    heatmapTime: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      paddingRight: 6,
      fontSize: 10,
      fontWeight: 500,
      color: MONTHLY_REPORT_PALETTE.textSecondary
    },

    heatmapCell: {
      borderRadius: 5,
      height: 24
    },

    heatmap0: {
      background: MONTHLY_REPORT_PALETTE.gray100
    },

    heatmap1: {
      background: MONTHLY_REPORT_PALETTE.primary100
    },

    heatmap2: {
      background: MONTHLY_REPORT_PALETTE.primary200
    },

    heatmap3: {
      background: MONTHLY_REPORT_PALETTE.primary300
    },

    heatmap4: {
      background: MONTHLY_REPORT_PALETTE.primary500
    },

    legendRow: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginTop: 12,
      justifyContent: "center"
    },

    legendText: {
      fontSize: 10,
      color: MONTHLY_REPORT_PALETTE.textSecondary
    },

    legendBox: {
      width: 18,
      height: 10,
      borderRadius: 3,
      display: "inline-block"
    },

    legendBox0: {
      background: MONTHLY_REPORT_PALETTE.gray100
    },

    legendBox1: {
      background: MONTHLY_REPORT_PALETTE.primary100
    },

    legendBox2: {
      background: MONTHLY_REPORT_PALETTE.primary200
    },

    legendBox3: {
      background: MONTHLY_REPORT_PALETTE.primary300
    },

    legendBox4: {
      background: MONTHLY_REPORT_PALETTE.primary500
    },

    spinnerWrapper: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(6)
    },

    errorBox: {
      padding: theme.spacing(3),
      borderRadius: 16,
      color: MONTHLY_REPORT_PALETTE.primary500,
      backgroundColor: MONTHLY_REPORT_PALETTE.primary50,
      border: `1px solid ${MONTHLY_REPORT_PALETTE.primary100}`
    },

    emptyBox: {
      padding: theme.spacing(3),
      borderRadius: 16,
      color: MONTHLY_REPORT_PALETTE.gray700,
      backgroundColor: MONTHLY_REPORT_PALETTE.surface,
      boxShadow: theme.shadows[1]
    },
    hiddenExportHost: {
      position: "absolute",
      left: "-100000px",
      top: 0,
      width: "0",
      height: "0",
      overflow: "hidden",
      pointerEvents: "none",
      opacity: 0
    }
  });
